/**
 * Recents Data Hook
 *
 * TanStack Query hook for fetching recent items.
 * This is invalidated by WebSocket entity change events,
 * ensuring the recents list stays up-to-date in real-time.
 *
 * `scope: 'mine'` asks the server for what the caller created OR edited,
 * ordered by their own last touch (the audit log; `recent-touches.ts`). Prefer
 * it to `createdById`, which only sees what the caller created and filters a
 * 3x over-fetch on the client.
 */

import { useQuery } from '@tanstack/react-query';
import { searchService, type SearchResult, type EntityType } from '../../services/search.service';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { shouldRetrySearch } from './useSearchQuery';

// Query key for recents - will be invalidated when any entity changes
export const recentsQueryKey = (
  workspaceId: string,
  entityTypes?: EntityType[],
  createdById?: string,
  scope?: 'mine',
  limit?: number,
) => ['recents', { workspaceId, entityTypes, createdById, scope, limit }] as const;

/**
 * Fetch recent items for the current workspace.
 *
 * `createdById` semantics:
 *   - `undefined` — no owner filter; return whatever the workspace
 *     surfaces. (Existing callers — search dialog seed list, etc.)
 *   - `''` (empty string) — caller *wants* an owner filter but the
 *     id is still loading. Query is disabled so the user doesn't
 *     briefly see other authors' rows in a section labelled "your
 *     recent work" before the real id arrives. Mirrors the
 *     pattern Bower uses elsewhere for "wait for the id" gating.
 *   - non-empty string — filter results to that user, post-filtering
 *     a 3× over-fetch client-side (backend has no createdBy filter
 *     today; see the file-level TODO + planning doc for the
 *     server-side follow-up).
 */
export function useRecents(
  limit: number = 10,
  entityTypes?: EntityType[],
  createdById?: string,
  options?: { enabled?: boolean; scope?: 'mine'; groupGenerated?: boolean },
) {
  const workspaceId = useWorkspaceId();
  const filterRequested = createdById !== undefined;
  const filterReady = filterRequested && createdById !== '';
  const fetchLimit = filterReady ? limit * 3 : limit;

  return useQuery({
    // `limit` is in the key: the dashboard asks 12 and the palette 8 of the same list.
    queryKey: [
      ...recentsQueryKey(workspaceId || '', entityTypes, createdById, options?.scope, limit),
      options?.groupGenerated ?? false,
    ],
    queryFn: async ({ signal }) => {
      const results = await searchService.search({
        query: '',
        limit: fetchLimit,
        sortBy: 'recent',
        entityTypes,
        scope: options?.scope,
        groupGenerated: options?.groupGenerated,
        signal,
      });
      if (!filterReady) return results;
      return results.filter((r) => r.createdById === createdById).slice(0, limit);
    },
    // Disable until the caller has supplied a non-empty owner id when
    // they explicitly asked for filtering. Without this gate the
    // initial unfiltered page paints before the real id resolves and
    // the user briefly sees coworkers' rows in "your recent work".
    // `options.enabled` lets callers that render eagerly but show recents
    // lazily (CommandPalette is mounted ~10× across the shell, closed)
    // defer the fetch until they're actually visible.
    enabled: !!workspaceId && (!filterRequested || filterReady) && (options?.enabled ?? true),
    // Keep recents fresh but don't refetch too aggressively
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: shouldRetrySearch,
  });
}

export type { SearchResult };
